import type { PlgHomeProduct } from '@/domains/home/apis/home-api';
import { getControlPlgHome, getIpHomeHref } from '@/domains/home/plg-home-routing';
import { HomeService } from '@/domains/home/services/home-service';
import { SiteRegion } from '@eureka/services/src/types/region';
import { E_ShareType } from '@eureka/services/src/types/share';
import { NAV_MENU_TYPE } from '@eureka/shared/nav-bar';
import { isOverseaOrStage } from '@eureka/shared/plg-plan/src/constants';
import { AuthService } from '@eureka/shared/share-tools/logic';
import { controlDeferred } from '@eureka/utils/control';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { Site, SLD } from '@patsnap-utils/biz';
import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import { deprecatedRouter } from './deprecated-router';
import { ipRouterList } from './ip-router';
import { proxyRouterList } from './proxy-router';

function redirectToPlgHome(product: PlgHomeProduct, to: RouteLocationNormalized, next: NavigationGuardNext) {
    console.debug('[PLG home routing] Redirecting to product home', { product });
    if (product === 'RD') {
        next({
            name: 'rd-home',
            query: to.query,
            replace: true,
        });
        return;
    }

    window.location.href = getIpHomeHref(to.query);
    next(false);
}

// TODO: 定时任务上线时恢复路由守卫及下方 cron 路由，并重新引入 getMonitorEntryTarget 与 NavigationGuard。
// const cronRouteBeforeEnter: NavigationGuard = async(_to, _from, next) => {
//     if (!await isUserLogin()) {
//         return next('/home');
//     }
//     if (await getMonitorEntryTarget() !== 'cron') {
//         window.location.href = '/monitor/#/my?from=cron';
//         return next(false);
//     }
//     next();
// };

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            component: () => import('@/pages/Layout.vue'),
            redirect: '/home',
            children: [
                deprecatedRouter,
                {
                    path: '/home',
                    name: 'home',
                    component: () => import('@/pages/home/ClawHome.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();

                        if (!await isUserLogin()) {
                            next();
                            return;
                        }

                        try {
                            const control = await controlDeferred.promise;
                            const product = getControlPlgHome(control.plg_home);
                            if (product) {
                                redirectToPlgHome(product, to, next);
                            } else {
                                next();
                            }
                        } catch (error) {
                            console.error('[PLG home routing] Failed to redirect from home', error);
                            if (!getControlPlgHome(to.query.start_from)) {
                                next();
                            }
                        }
                    },
                },
                {
                    path: '/rd-home',
                    name: 'rd-home',
                    component: () => import('@/pages/home/RdHome.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();

                        if (!await isUserLogin()) {
                            console.debug('[RD home routing] Guest mode detected, skipping onboarding check');
                            next();
                            return;
                        }

                        const { need_guide } = await controlDeferred.promise;
                        if ((need_guide || []).includes('air_onboarding')) {
                            next({
                                name: 'onboarding',
                                query: {
                                    redirect: to.fullPath,
                                },
                                replace: true,
                            });
                            return;
                        }
                        next();
                    },
                },
                {
                    path: '/subscribe',
                    name: 'subscribe',
                    component: () => import('@/pages/home/CreatePayment.vue'),
                },
                {
                    path: '/rd-gallery',
                    name: 'gallery',
                    meta: {
                        navModuleType: NAV_MENU_TYPE.COMMUNITY,
                    },
                    component: () => import('@/pages/gallery/index.vue'),
                },
                {
                    path: '/check-payment-status',
                    name: 'check-payment-status',
                    component: () => import('@/pages/home/CheckPaymentStatus.vue'),
                },
                {
                    path: '/ls',
                    name: 'ls',
                    component: () => import('@/pages/home/LS.vue'),
                    beforeEnter: async(to, from, next) => {
                        const searchParams = new URLSearchParams(location.search)
                        location.href = `/ls/#/enterprise${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`
                        next(false)
                    },
                },
                {
                    path: '/ls/home',
                    name: 'ls-home',
                    component: () => import('@/pages/home/LSHome.vue'),
                    beforeEnter: async(to, from, next) => {
                        const searchParams = new URLSearchParams(location.search)
                        location.href = `/ls/#/home${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`
                        next(false)
                    },
                },
                {
                    path: '/mse',
                    name: 'mse',
                    component: () => import('@/pages/home/MSE.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        window.location.href = '/mse/#/home'
                        next(false);
                    },
                },
                ...ipRouterList,

                {
                    path: '/agent-list',
                    name: 'agent-list',
                    component: () => import('@/pages/agent-list/index.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.AGENT_LIST,
                    },
                },
                {
                    path: '/customize-list',
                    name: 'customize-list',
                    component: () => import('@/pages/customize-list/index.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.CUSTOMIZE_LIST,
                    },
                },
                {
                    path: '/share/skill/:shareCode',
                    name: 'share-skill',
                    component: () => import('@/pages/share-skill/index.vue'),
                },
                // TODO: 定时任务上线时恢复列表与详情路由。
                // {
                //     path: '/cron',
                //     name: 'cron',
                //     component: () => import('@/pages/cron/index.vue'),
                //     meta: {
                //         navModuleType: NAV_MENU_TYPE.MONITOR,
                //     },
                //     beforeEnter: cronRouteBeforeEnter,
                // },
                // {
                //     path: '/cron/:jobId',
                //     name: 'cron-detail',
                //     component: () => import('@/pages/cron/detail.vue'),
                //     meta: {
                //         navModuleType: NAV_MENU_TYPE.MONITOR,
                //     },
                //     beforeEnter: cronRouteBeforeEnter,
                // },
                {
                    path: '/credits',
                    name: 'credits',
                    component: () => import('@/pages/credits/index.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.CREDITS,
                    },
                    beforeEnter: async(to, from, next) => {
                        const loggedIn = await isUserLogin();
                        if (!loggedIn) {
                            return next('/home');
                        }

                        const control = await controlDeferred.promise;
                        if (!control.credit_package) {
                            return next({ name: 'home' });
                        }

                        next();
                    },
                },
                {
                    path: '/credits-price',
                    name: 'credits-price',
                    component: () => import('@/pages/credits-price/index.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.CREDITS_PRICE,
                        hideNavBar: true,
                    },
                    beforeEnter: async(to, _from, next) => {
                        try {
                            const control = await controlDeferred.promise;
                            if (getControlPlgHome(control.plg_home) === 'RD') {
                                next({
                                    name: 'rd-pricing',
                                    query: to.query,
                                    replace: true,
                                });
                                return;
                            }
                            // IP画像用户跳转到ip的pricing页面
                            if (getControlPlgHome(control.plg_home) === 'IP') {
                                next({
                                    name: 'ip-pricing-page',
                                    query: to.query,
                                    replace: true,
                                });
                                return;
                            }
                        } catch (error) {
                            console.error('[Pricing routing] Failed to resolve PLG home', error);
                        }
                        next();
                    },
                },
                {
                    path: '/rd-pricing',
                    name: 'rd-pricing',
                    component: () => import('@/pages/rd-pricing/index.vue'),
                    meta: {
                        hideNavBar: true,
                    },
                },
                {
                    path: '/ip-pricing',
                    name: 'ip-pricing-page',
                    component: () => import('@/pages/ip-pricing/index.vue'),
                    meta: {
                        hideNavBar: true,
                    },
                },
                {
                    path: '/account-status',
                    name: 'account-status',
                    component: () => import('@/pages/account-status/index.vue'),
                    meta: {
                        // hideNavBar: true,
                    },
                },
                {
                    path: '/contact-admin',
                    name: 'contact-admin',
                    component: () => import('@/pages/contact-admin/index.vue'),
                    meta: {
                        // hideNavBar: true,
                    },
                },
                {
                    path: '/user-profile',
                    name: 'user-profile',
                    component: () => import('@/pages/UserProfile.vue'),
                },
                {
                    name: 'seek',
                    path: '/page/:seek_id',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.seek_id as string,
                            data_type: E_ShareType.SEEK_SEARCH,
                        });
                        next();
                    },
                    component: () => import('@/pages/seek/Page.vue'),
                },
                {
                    name: 'generalSearch',
                    path: '/general-search',
                    component: () => import('@/pages/general-search/index.vue'),
                },
                {
                    name: 'materialsGeneralSearch',
                    path: '/materials-general-search',
                    component: () => import('@/pages/materials-general-search/index.vue'),
                },
                {
                    name: 'ai-ls',
                    path: '/ai-ls',
                    children: [
                        {
                            path: ':lsAgentName',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-entry',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lsAgentName/:lsAgentId',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-detail',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lifesciences*',
                            name: 'ai-ls-page',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        }
                    ],
                    beforeEnter(to, from, next) {
                        location.href = `/ls/#${to.fullPath}`
                        next(false)
                    },
                },
                {
                    name: 'workflow',
                    path: '/material/workflow',
                    component: () => import('@/pages/material/workflow/index.vue'),
                },
                {
                    name: 'substance',
                    path: '/material/substance',
                    component: () => import('@/pages/material/substance/index.vue'),
                },
                {
                    name: 'property',
                    path: '/material/property',
                    component: () => import('@/pages/material/property/index.vue'),
                },
                {
                    name: 'structure',
                    path: '/material/structure',
                    component: () => import('@/pages/material/structure/index.vue'),
                },
                {
                    name: 'metal',
                    path: '/material/metal',
                    component: () => import('@/pages/material/metal/index.vue'),
                },
                {
                    name: 'grade',
                    path: '/material/grade/:grade_id',
                    component: () => import('@/pages/material/grade/index.vue'),
                },
                {
                    name: 'mse-doc-analyzer',
                    path: '/material/analyzer',
                    component: () => import('@/pages/material/doc-analyzer/MSEDocAnalyzer.vue'),
                },
                {
                    name: 'mse-doc-analyzer-result',
                    path: '/material/analyzer/:lsAgentId',
                    component: () => import('@/pages/material/doc-analyzer/MSEDocAnalyzer.vue'),
                },
                {
                    name: 'userSetting',
                    path: '/user-setting',
                    component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Layout.vue'),
                    beforeEnter: async(to, from, next) => {
                        if (to.name === 'userSettingPackage') {
                            if (await isUserLogin()) {
                                const [control, account] = await Promise.all([controlDeferred.promise, accountDeferred.promise]);
                                if (control.credit_package && !account?._info?.company_id) {
                                    window.location.href = new Site({ sld: SLD.EUREKA, pathname: 'credits' }).href;
                                    return next(false);
                                }
                            }
                            try {
                                const homeService = injectService<HomeService>(HomeService);
                                const data = await homeService.getHomePageType();
                                return next({
                                    name: `userSettingPackage${data.homepage_type === 'RD_PLG' ? 'RD' : data.homepage_type}`,
                                    query: to.query,
                                })
                            } catch (error) {
                                return next({
                                    name: 'userSettingPackageRD',
                                    query: to.query,
                                })
                            }
                        }
                        next();
                    },
                    children: [
                        {
                            path: 'amount',
                            name: 'userSettingAmount',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Amount.vue'),
                            beforeEnter: async(to, from, next) => {
                                const loggedIn = await isUserLogin();
                                if (loggedIn) {
                                    const data = await accountDeferred.promise;
                                    if (!data._info?.company_id) {
                                        next('/home');
                                    }
                                }
                                next()
                            },
                        },
                        {
                            path: 'package',
                            name: 'userSettingPackage',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Package.vue'),
                        },
                        {
                            path: 'package/management',
                            name: 'userSettingPackageManagement',
                            component: () => import(/* webpackChunkName: "air-usersetting-management" */'@/pages/user-setting/new-version/product-lines/Management.vue'),
                            beforeEnter: async(to, from, next) => {
                                // CN 环境拦截：个人支付和订阅管理仅在海外环境可用
                                if (!isOverseaOrStage) {
                                    // CN 环境重定向到套餐页面
                                    next('/user-setting/package');
                                    return;
                                }
                                next();
                            },
                        },
                        {
                            path: 'package/rd',
                            name: 'userSettingPackageRD',
                            component: () => import(/* webpackChunkName: "air-usersetting-RD" */'@/pages/user-setting/new-version/product-lines/RD.vue'),
                        }, {
                            path: 'package/ip',
                            name: 'userSettingPackageIP',
                            component: async() => {
                                const data = await accountDeferred.promise;
                                if (data.siteRegion === SiteRegion.CN) {
                                    return import(/* webpackChunkName: "air-usersetting-IP" */'@/pages/user-setting/new-version/product-lines/IP.vue')
                                }
                                return import(/* webpackChunkName: "air-usersetting-IpPlg" */'@/pages/user-setting/new-version/product-lines/IpPlg.vue')
                            },
                        }, {
                            path: 'package/mse',
                            name: 'userSettingPackageMSE',
                            component: () => import(/* webpackChunkName: "air-usersetting-MSE" */'@/pages/user-setting/new-version/product-lines/MSE.vue'),
                        }, {
                            path: 'package/ls',
                            name: 'userSettingPackageLS',
                            component: () => import(/* webpackChunkName: "air-usersetting-LS" */'@/pages/user-setting/new-version/product-lines/LS.vue'),
                            beforeEnter(to, from, next) {
                                location.href = `/ls/#${to.fullPath}`
                                next(false)
                            },
                        }, {
                            path: 'package/ls/plg',
                            name: 'userSettingPackageLS_PLG',
                            component: () => import(/* webpackChunkName: "air-usersetting-LSPlg" */'@/pages/user-setting/new-version/product-lines/LsPlg.vue'),
                            beforeEnter(to, from, next) {
                                location.href = `/ls/#${to.fullPath}`
                                next(false)
                            },
                        }, {
                            path: 'agent-settings',
                            name: 'userSettingAgentSettings',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/AgentSettings.vue'),
                        }, {
                            path: 'plan',
                            name: 'userSettingPlan',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/PlgPlan.vue'),
                        }
                    ],
                },
                {
                    name: 'CompetitiveLandscape',
                    path: '/competitive-landscape',
                    component: () => import('@/pages/competitive-landscape/index.vue'),
                },
                {
                    name: 'CompanySearch',
                    path: '/company-search',
                    component: () => import('@/pages/company-search/index.vue'),
                },
                {
                    name: 'CompanyScout',
                    path: '/company-scout',
                    component: () => import('@/pages/company-scout/index.vue'),
                },
                {
                    name: 'CompanyScoutDetail',
                    path: '/company-scout/:company_scout_id',
                    component: () => import('@/pages/company-scout/detail.vue'),
                },
                {
                    name: 'AiSearch',
                    path: '/ai-search',
                    redirect: '/home',
                },
                {
                    name: 'AiSearchResult',
                    path: '/ai-search/:conversation_id',
                    component: () => import('@/pages/ai-search/detail.vue'),
                    // 权限检查已移至组件内部，避免阻塞页面导航
                },
                {
                    path: '/403',
                    component: () => import('@/pages/403/Unauthorized.vue'),
                },
                {
                    path: '/404',
                    component: () => import('@/pages/404/NotFound.vue'),
                }
            ],
        },
        // {
        //     name: 'research-mcp',
        //     path: '/research-mcp',
        //     component: () => import('@/components/research-mcp/ResearchMcp.vue'),
        // },
        {
            path: '/ai',
            name: 'ai',
            redirect: '/home',
            component: () => import('@/pages/Layout.vue'),
            meta: {
                navModuleType: NAV_MENU_TYPE.AI_GENERATE,
            },
            children: [
                {
                    path: '/ai/agent',
                    name: 'ai-agent',
                    redirect: '/home',
                },
                {
                    path: '/ai/agent-flow/:agent_job_id',
                    name: 'ai-agent-flow',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.agent_job_id as string,
                            data_type: E_ShareType.AI_AGENT,
                        });
                        next();
                    },
                    component: () => import('@/pages/ai/AgentFlow.vue'),
                }
            ],
        },
        ...proxyRouterList,
        // 通配符路由 - 捕获所有未匹配的路径并重定向到404页面
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            redirect: '/404',
        }
    ],
});

export default router;
